import {
  IGlobalActions,
  IInteractiveState,
  IMeta,
  IReduxAction,
} from '@bees-web/core/types'
import { MouseEvent } from 'react'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import {
  RATE_MY_DELIVERY_MODAL_CHANGE,
  RATE_MY_DELIVERY_MODAL_VIEWED,
  RATE_MY_DELIVERY_RATED,
  RATE_MY_DELIVERY_SUBMISSION_COMPLETED,
  RATE_MY_DELIVERY_SUBMISSION_FAILED,
} from '../../constants/ratings'
import { IRatings, ITrackableAction } from '../../interfaces/ratings'

export const rateMyDeliveryChange = (payload: object): IReduxAction => ({
  type: RATE_MY_DELIVERY_MODAL_CHANGE,
  payload,
})

export const rateMyDeliveryRated = (orderNumber: string): IReduxAction => ({
  type: RATE_MY_DELIVERY_RATED,
  payload: {
    orderNumber,
  },
})

export const rateMyDeliveryResetState = rateMyDeliveryChange({
  isShowing: false,
  comment: '',
  score: 0,
  skipped: false,
  answers: [{ questionId: '', responses: [] }],
  modalStatus: 'initial',
  survey: {},
  submittedMessage: '',
})

export const rateMyDeliverySubmitRating = (
  e: MouseEvent<HTMLButtonElement>,
  meta: IMeta,
  interactiveState: IInteractiveState<IRatings>,
  globalActions: IGlobalActions,
  dispatch: ThunkDispatch<IInteractiveState<IRatings>, {}, AnyAction>,
  skip: boolean = false
) => {
  const { currentTarget } = e
  currentTarget.setAttribute('disabled', 'true')
  const { deliveryDate, orderId, vendorId } = meta
  const { accountId } = interactiveState.globals.authentication.selectedAccount

  let { score, comment, skipped, answers } =
    interactiveState.state.ratings.rateMyDelivery
  if (skip) {
    score = 0
    comment = ''
    skipped = true
    answers = []
  }
  dispatch(
    globalActions.requester.doRequest({
      ...meta.request,
      body: {
        ...meta.request.body,
        score,
        comment,
        skipped,
        answers: score < 5 ? answers : [],
        accountId,
      },
      onError: onError({
        dispatch,
        skip,
        deliveryDate,
        orderId,
        accountId,
        score,
        comment,
        answers,
        vendorId,
      }),
      finally: finallyRequest({
        skip,
        dispatch,
        deliveryDate,
        orderId,
        vendorId,
        accountId,
        score,
        comment,
        answers,
      }),
    })
  )
  dispatch(rateMyDeliveryRated(orderId))
}

export const rateMyDeliverySubmitDismiss = (
  e: MouseEvent<HTMLButtonElement>,
  meta: IMeta,
  interactiveState: IInteractiveState<IRatings>,
  globalActions: IGlobalActions,
  dispatch: ThunkDispatch<IInteractiveState<IRatings>, {}, AnyAction>
) => {
  if (interactiveState.state.ratings.rateMyDelivery.modalStatus === 'initial') {
    rateMyDeliverySubmitRating(
      e,
      meta,
      interactiveState,
      globalActions,
      dispatch,
      true
    )
  }
  dispatch(rateMyDeliveryResetState)
}

export const trackDeliveryRatingModalViewed = ({
  delivery_date = null,
  order_id = null,
  poc_id = null,
  vendorId = null,
  rating_reason = null,
  english_translation_rating_reason = null,
  native_language_rating_reason = null,
  rating_free_text = null,
  rating_given = null,
  failure_reason = undefined,
}): ITrackableAction => ({
  type: RATE_MY_DELIVERY_MODAL_VIEWED,
  payload: {
    delivery_date,
    order_id,
    poc_id,
    vendorId,
    referrer: 'Login',
    screen_name: 'Home',
    rating_reason,
    rating_maximum: 5,
    english_translation_rating_reason,
    native_language_rating_reason,
    rating_free_text,
    rating_given,
    failure_reason,
  },
})

export const trackDeliveryRatingSubmission = ({
  delivery_date = null,
  order_id = null,
  poc_id = null,
  vendorId = null,
  rating_reason = null,
  english_translation_rating_reason = null,
  native_language_rating_reason = null,
  rating_free_text = null,
  rating_given = null,
  failure_reason = undefined,
  type,
}): ITrackableAction => ({
  type,
  payload: {
    delivery_date,
    order_id,
    poc_id,
    vendorId,
    referrer: 'Login',
    screen_name: 'Home',
    rating_reason,
    rating_maximum: 5,
    english_translation_rating_reason,
    native_language_rating_reason,
    rating_free_text,
    rating_given,
    failure_reason,
  },
})

export const onError =
  ({
    dispatch,
    skip,
    deliveryDate,
    orderId,
    accountId,
    score,
    comment,
    answers,
    vendorId,
  }) =>
  async ({ response }) => {
    if (skip === false) {
      dispatch(rateMyDeliveryChange({ modalStatus: 'error' }))
      const failure_reason = (await response.json()).message
      dispatch(
        trackDeliveryRatingSubmission({
          delivery_date: deliveryDate,
          order_id: orderId,
          vendorId,
          poc_id: accountId,
          rating_given: score,
          native_language_rating_reason: comment,
          rating_free_text: comment,
          rating_reason: answers[0]?.responses.join(','),
          failure_reason,
          type: RATE_MY_DELIVERY_SUBMISSION_FAILED,
        })
      )
    }
  }

export const finallyRequest =
  ({
    skip,
    dispatch,
    deliveryDate,
    orderId,
    vendorId,
    accountId,
    score,
    comment,
    answers,
  }) =>
  ({ response }) => {
    if (skip === false && response.ok) {
      dispatch(rateMyDeliveryChange({ modalStatus: 'submitted' }))
      dispatch(
        trackDeliveryRatingSubmission({
          delivery_date: deliveryDate,
          order_id: orderId,
          vendorId,
          poc_id: accountId,
          rating_given: score,
          native_language_rating_reason: comment,
          rating_free_text: comment,
          rating_reason: answers[0]?.responses.join(','),
          type: RATE_MY_DELIVERY_SUBMISSION_COMPLETED,
        })
      )
    }
  }
